/**
 * Central session-dead gate — one sign-out surface, stops background retry loops.
 * In-memory terminal latch (never persisted). While latched, routing must not
 * treat localStorage tokens as authenticated.
 */

import { tokenFpSync } from './authTokens';
import { logSessionDeadTransition, readLocalStorageRefreshFp } from './authDiagnostics';
import { interceptInvoluntaryLogout } from './authDeclawShadow';

let _sessionDead = false;
let _sessionExpiredDispatched = false;
let _sideEffectsRegistered = false;
/** Fingerprint of the refresh token that triggered terminal session-dead. */
let _poisonedRefreshFp: string | null = null;

export function isSessionDead(): boolean {
  return _sessionDead;
}

export function isPoisonedRefreshToken(token: string | null | undefined): boolean {
  if (!_poisonedRefreshFp || !token) return false;
  return tokenFpSync(token) === _poisonedRefreshFp;
}

/** Remove cached auth — localStorage is cache only, never routing source of truth. */
export function clearStaleAuthCache(): void {
  try { localStorage.removeItem('maestro_auth'); } catch { /* ignore */ }
}

/** Mark session dead and dispatch maestro:session-expired at most once. */
export function markSessionDead(source?: string): void {
  if (interceptInvoluntaryLogout('session_mark_dead', source ?? 'unknown')) {
    return;
  }
  const alreadyDead = _sessionDead;
  if (!_sessionDead) {
    _sessionDead = true;
    const fp = readLocalStorageRefreshFp();
    if (fp) _poisonedRefreshFp = fp;
    try { logSessionDeadTransition('enter', fp, source); } catch { /* noop */ }
  }
  clearStaleAuthCache();
  if (alreadyDead || _sessionExpiredDispatched) return;
  _sessionExpiredDispatched = true;
  try {
    window.dispatchEvent(new CustomEvent('maestro:session-expired', {
      detail: { source: source ?? 'unknown' },
    }));
  } catch { /* ignore */ }
}

export function resetSessionDeadState(): void {
  if (!_sessionDead && !_sessionExpiredDispatched && !_poisonedRefreshFp) return;
  try { logSessionDeadTransition('exit', readLocalStorageRefreshFp(), 'sign_in'); } catch { /* noop */ }
  _sessionDead = false;
  _sessionExpiredDispatched = false;
  _poisonedRefreshFp = null;
  try {
    window.dispatchEvent(new CustomEvent('maestro:session-dead-cleared'));
  } catch { /* ignore */ }
}

/**
 * Stop BATON reconnect, radio refill, and similar loops when auth is revoked.
 * Covers 401/403 refresh rejection (api.ts markSessionDead) even when the BATON
 * WebSocket only reports abnormal closure 1006 with an empty reason — do not rely
 * on WS close-code auth detection for that path.
 */
export function registerSessionDeadSideEffects(): void {
  if (_sideEffectsRegistered || typeof window === 'undefined') return;
  _sideEffectsRegistered = true;
  window.addEventListener('maestro:session-expired', () => {
    void import('../radio/radioRefill').then((m) => m.clearRadioSession()).catch(() => {});
    void import('../handlers/ClientTransportHandler')
      .then((m) => m.getClientTransport().haltReconnectLoops())
      .catch(() => {});
  });
}
